import { MenuTitles, Pages } from 'constants/menu';
import { AgencyContext } from 'contexts';
import { ContentLayout, PageLayout } from 'layout';
import { AgxToast, enumToOptions } from '@urbanx/agx-ui-components';
import { useMutation } from '@tanstack/react-query';
import { useContext, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { StaffAction } from 'types/staff';
import { AgxToastState, NotificationState } from 'types/commonTypes';
import { UploadPublicDocument } from 'services';
import { isEqual } from 'lodash-es';

import { FormPrompt } from '../../components/FormPrompt';
import RoleAssignmentModal from './RoleAssignmentModel';
import StaffMemberFormPage, {
  type StaffMemberFormHandle,
  type StaffMemberFormValues,
  type StaffMemberSubmissionResult,
} from './StaffMemberFormPage';
import { useDoesUserHasRequiredRoles } from 'helpers/doesUserHaveRequiredRoles';
import { AuthContext, StaffRole } from '@urbanx/agx.auth.client';
import {
  UploadedDocument,
  useCreateStaffMember,
  useInvalidateUseGetAllStaff,
  useInvalidateUseGetStaffByRoles,
} from '@urbanx/agx.staff.client';
import { getEnumValueByValue } from 'helpers/enumHelper';
import {
  AgenciesApiContext,
  UrbanXTeamByAgenciesResponse,
  useAssignStaffMemberToAgencies,
  useGetAllAgencies,
  useInvalidateUseGetUrbanXTeamForAgency,
  useInvalidateUseGetStaffMembersAssignedToAgencies,
  useRemovePublicDocument,
} from '@urbanx/agx.agencies.client';
import {
  AgxButton,
  AgxEmptyState,
  AgxSpacer,
  AgxSaveChangesButton,
  Icon_Arrow,
  SAVE_BUTTON_STATES,
} from '@urbanx/agx.ui-components2';
import {
  getAvailableStaffRoleOptions,
  mapAgenciesToOptions,
  rolesAssignableToAgency,
  staffStateOptions,
  dedupeRoleConflicts,
} from './staffPageUtils';

const emptyStaffMember: StaffMemberFormValues = {};
const staffRoleOptions = enumToOptions(StaffRole);

const CreateStaffMember = () => {
  const navigate = useNavigate();
  const [selectedAgency] = useContext(AgencyContext);
  const { userAccount, getAuthToken } = useContext(AuthContext);
  const onBack = () => navigate(Pages.STAFF);

  const invalidateGetAllStaff = useInvalidateUseGetAllStaff();
  const invalidateStaffByRoles = useInvalidateUseGetStaffByRoles();
  const invalidateUrbanXTeamForAgency = useInvalidateUseGetUrbanXTeamForAgency();
  const invalidateGetStaffMembersAssignedToAgencies =
    useInvalidateUseGetStaffMembersAssignedToAgencies();

  const formRef = useRef<StaffMemberFormHandle>(null);
  const [staffMemberForm, setStaffMemberForm] = useState<StaffMemberFormValues>({
    ...emptyStaffMember,
    roles: emptyStaffMember.roles,
  });
  const [img, setImg] = useState<string | undefined>(undefined);
  const [isBoundFormDirty, setIsBoundFormDirty] = useState(false);
  const [agencyBaseline, setAgencyBaseline] = useState<string[]>([]);
  const [hasPhotoChanges, setHasPhotoChanges] = useState(false);
  const [forceCheck, setForceCheck] = useState(false);
  const [showWarningModal, setShowWarningModal] = useState(false);
  const [pendingRoleAssignmentCheck, setPendingRoleAssignmentCheck] = useState(false);
  const [roleConflicts, setRoleConflicts] = useState<UrbanXTeamByAgenciesResponse[]>([]);
  const [roleConflictRoles, setRoleConflictRoles] = useState<string[]>([]);
  const [saveButtonState, setSaveButtonState] = useState<SAVE_BUTTON_STATES>(
    SAVE_BUTTON_STATES.UNSAVED
  );
  const [agencyIds, setAgencyIds] = useState<string[]>([]);
  const [createdStaffRoute, setCreatedStaffRoute] = useState<
    { id: string; staff: StaffMemberFormValues } | undefined
  >(undefined);
  const submissionResolver = useRef<((result: StaffMemberSubmissionResult) => void) | undefined>(
    undefined
  );
  const pendingSubmission = useRef<
    { staffMember: StaffMemberFormValues; agencyIds: string[] } | undefined
  >(undefined);
  const [toastState, setToastState] = useState<AgxToastState>({
    color: 'success',
    message: '',
    open: false,
  });

  const doesUserHaveRequiredRoles = useDoesUserHasRequiredRoles();

  const availableStaffRoleOptions = getAvailableStaffRoleOptions(
    staffRoleOptions,
    doesUserHaveRequiredRoles
  );

  const selectedRoles = staffMemberForm.roles;

  const roleIsAssignableToAgency =
    selectedRoles?.some((role) => rolesAssignableToAgency.includes(role)) ?? false;

  const hasAgencyChanges = !isEqual(agencyIds, agencyBaseline);
  const isFormDirty = isBoundFormDirty || hasAgencyChanges || hasPhotoChanges;

  const resolveSubmission = (result: StaffMemberSubmissionResult) => {
    submissionResolver.current?.(result);
    submissionResolver.current = undefined;
    pendingSubmission.current = undefined;
  };

  const { data: agencies, isPending: agenciesPending } = useGetAllAgencies();

  const agenciesApi = useContext(AgenciesApiContext);

  const { createStaffMember, createStaffMemberPending } = useCreateStaffMember({
    onSuccess: async (createdStaffMember, inputData) => {
      const createdIdentifier = createdStaffMember?.id || inputData?.id || inputData?.email;
      const createdStaff = {
        ...inputData,
        id: createdStaffMember?.id ?? inputData?.id,
        email: createdStaffMember?.email ?? inputData?.email,
      } as StaffMemberFormValues;

      const completeCreation = () => {
        if (createdIdentifier) setCreatedStaffRoute({ id: createdIdentifier, staff: createdStaff });
        setAgencyBaseline(pendingSubmission.current?.agencyIds ?? []);
        setHasPhotoChanges(false);
        resolveSubmission({ status: 'accepted' });
      };

      try {
        const email = inputData?.email;
        const roles = inputData?.roles ?? [];
        const submittedAgencyIds = pendingSubmission.current?.agencyIds ?? [];
        const submittedAssignableRoles = roles.filter((role) =>
          rolesAssignableToAgency.includes(role)
        );

        if (!email || roles.length === 0) {
          setSaveButtonState(SAVE_BUTTON_STATES.ERROR);
          resolveSubmission({ status: 'failed', error: new Error('Missing staff account data') });
          return;
        }

        const staffRoles = roles
          .map((role) => getEnumValueByValue(StaffRole, role))
          .filter((role): role is StaffRole => role !== undefined);

        if (staffRoles.length === 0) {
          setSaveButtonState(SAVE_BUTTON_STATES.ERROR);
          resolveSubmission({ status: 'failed', error: new Error('Missing staff roles') });
          return;
        }

        if (submittedAssignableRoles.length > 0) {
          await assignStaffMemberToAgencies({
            staffMemberId: email,
            staffRoles,
            agencyIds: submittedAgencyIds,
          });
        }

        await Promise.all([
          ...submittedAssignableRoles.map((role) =>
            invalidateGetStaffMembersAssignedToAgencies(role, submittedAgencyIds)
          ),
          invalidateUrbanXTeamForAgency(),
          invalidateStaffByRoles(),
          invalidateGetAllStaff(),
        ]);

        setSaveButtonState(SAVE_BUTTON_STATES.SAVED);
        setToastState({
          color: 'success',
          message: 'Staff member created successfully',
          open: true,
        });

        completeCreation();
      } catch {
        setSaveButtonState(SAVE_BUTTON_STATES.ERROR);
        completeCreation();
        setToastState({
          color: 'error',
          message: 'Staff member created, but follow-up updates failed',
          open: true,
        });
      }
    },
    onError: (error) => {
      resolveSubmission({ status: 'failed', error });
      setSaveButtonState(SAVE_BUTTON_STATES.ERROR);
      setToastState({
        color: 'error',
        message: 'Error saving changes',
        open: true,
      });
    },
  });

  const { assignStaffMemberToAgencies, assignStaffMemberToAgenciesPending } =
    useAssignStaffMemberToAgencies({
      onSuccess: () => undefined,
      onError: () => {
        setSaveButtonState(SAVE_BUTTON_STATES.ERROR);
        setToastState({
          color: 'error',
          message: 'Error occurred assigning staff to agencies',
          open: true,
        });
      },
    });

  const confirmCreateStaffMember = () => {
    setShowWarningModal(false);
    setPendingRoleAssignmentCheck(false);
    if (pendingSubmission.current) {
      void createStaffMember(pendingSubmission.current.staffMember).catch(() => undefined);
    }
  };

  const checkCurrentRoleAssignments = async () => {
    const submission = pendingSubmission.current;
    if (!submission) return;

    const submittedAssignableRoles = (submission.staffMember.roles ?? []).filter((role) =>
      rolesAssignableToAgency.includes(role)
    );
    const shouldCheckAssignments =
      submittedAssignableRoles.length > 0 && submission.agencyIds.length > 0;

    if (shouldCheckAssignments) {
      try {
        const conflictResponses = await Promise.all(
          submittedAssignableRoles.map(async (role) => {
            const response = await agenciesApi.getStaffMembersAssignedToAgencies(
              role,
              submission.agencyIds
            );

            return {
              staffRole: response.staffRole ?? role,
              staffRoleAssignments: response.staffRoleAssignments ?? [],
            } as UrbanXTeamByAgenciesResponse;
          })
        );

        const dedupedRoleConflicts = dedupeRoleConflicts(conflictResponses);

        if (dedupedRoleConflicts.length > 0) {
          setRoleConflicts(dedupedRoleConflicts);
          setRoleConflictRoles(submittedAssignableRoles);
          setPendingRoleAssignmentCheck(false);
          setShowWarningModal(true);
          return;
        }
      } catch (error) {
        setPendingRoleAssignmentCheck(false);
        resolveSubmission({ status: 'failed', error });
        setSaveButtonState(SAVE_BUTTON_STATES.ERROR);
        setToastState({
          color: 'error',
          message: 'Error validating role assignments',
          open: true,
        });
        return;
      }
    }

    setRoleConflicts([]);
    confirmCreateStaffMember();
  };

  const onValidSubmit = (values: StaffMemberFormValues) => {
    setStaffMemberForm(values);
    pendingSubmission.current = { staffMember: values, agencyIds: [...agencyIds] };
    setSaveButtonState(SAVE_BUTTON_STATES.SAVING);
    setPendingRoleAssignmentCheck(true);
    return new Promise<StaffMemberSubmissionResult>((resolve) => {
      submissionResolver.current = resolve;
    });
  };

  useEffect(() => {
    if (!pendingRoleAssignmentCheck) {
      return;
    }

    void checkCurrentRoleAssignments();
  }, [pendingRoleAssignmentCheck, agenciesApi]);

  useEffect(() => {
    if (!createdStaffRoute || isFormDirty) return;

    navigate(`${Pages.EDIT_STAFF}/${encodeURIComponent(createdStaffRoute.id)}`, {
      replace: true,
      state: { createdStaff: createdStaffRoute.staff },
    });
  }, [createdStaffRoute, isFormDirty, navigate]);

  const { mutate: uploadPublicDocument, isPending: isUploading } = useMutation({
    mutationFn: UploadPublicDocument,
    onSuccess: (value) => {
      setImg(value?.publicPath || '');
      setStaffMemberForm((current) => ({ ...current, photo: value as UploadedDocument }));
      setHasPhotoChanges(true);
    },
    onError: () => {
      setToastState({
        color: 'error',
        message: 'Error uploading photo',
        open: true,
      });
    },
  });

  const onUploadPhoto = (file: File) => {
    setImg(URL.createObjectURL(file));
    return new Promise((resolve, reject) => {
      uploadPublicDocument(
        {
          selectedFile: file,
          AgencyId: 'UrbanX',
          getAuthToken,
        },
        {
          onSuccess: resolve,
          onError: reject,
        }
      );
    });
  };

  const { removePublicDocument: removePhoto, removePublicDocumentPending: isRemovingPhoto } =
    useRemovePublicDocument({
      onSuccess: () => {
        setImg(undefined);
        setStaffMemberForm((current) => ({ ...current, photo: undefined }));
        setHasPhotoChanges(true);
      },
      onError: () => {
        setToastState({
          color: 'error',
          message: 'Error removing photo',
          open: true,
        });
      },
    });

  const isSavingChanges =
    createStaffMemberPending ||
    assignStaffMemberToAgenciesPending ||
    pendingRoleAssignmentCheck ||
    showWarningModal;
  const isPhotoActionPending = isUploading || isRemovingPhoto;

  const saveChangesState = isSavingChanges
    ? SAVE_BUTTON_STATES.SAVING
    : saveButtonState === SAVE_BUTTON_STATES.ERROR
      ? SAVE_BUTTON_STATES.ERROR
      : isFormDirty
        ? SAVE_BUTTON_STATES.UNSAVED
        : saveButtonState;

  const agenciesOptions = mapAgenciesToOptions(agencies, agenciesPending);

  const onBackClick = () => {
    if (isFormDirty) {
      setForceCheck(true);
      return;
    }

    onBack();
  };

  return (
    <PageLayout
      agentName={userAccount?.firstName || ''}
      primaryPageTitle={selectedAgency?.name || ''}
      currentPageTitle={MenuTitles.TEAM}
      isStaff
    >
      <ContentLayout hasSecondaryMenu secondaryActiveMenu={MenuTitles.TEAM} isStaff>
        {agenciesPending ? (
          <AgxEmptyState variant="minimal" label="Loading" />
        ) : (
          <AgxSpacer direction="vertical" gap="spacing-5xl" fill>
            <FormPrompt
              hasUnsavedChanges={isFormDirty}
              forceCheck={forceCheck}
              resetForceCheck={(value: boolean) => setForceCheck(value)}
              onBack={onBack}
            />

            <AgxToast selector="#agxToast" toastState={toastState} />

            <div className="borderBottomContainer">
              <AgxButton
                id="return-button"
                variant="naked"
                text="Create Staff Member"
                leftIcon={<Icon_Arrow direction="left" height={22} width={22} />}
                onClick={onBackClick}
              />

              <AgxSpacer
                direction="horizontal"
                className="growOne"
                contentJustification="end"
                contentAlignment="center"
              >
                <AgxSaveChangesButton
                  id="btnSaveChanges"
                  text="Save Changes"
                  variant="primary"
                  size="medium"
                  state={saveChangesState}
                  onClick={() => void formRef.current?.submit()}
                  disabled={isPhotoActionPending || isSavingChanges || !isFormDirty}
                />
              </AgxSpacer>
            </div>

            <StaffMemberFormPage
              ref={formRef}
              pageAction={StaffAction.CREATE}
              staffMember={staffMemberForm}
              photoUri={img}
              isSaving={isSavingChanges}
              isPhotoActionPending={isPhotoActionPending}
              onValidSubmit={onValidSubmit}
              availableStaffRoleOptions={availableStaffRoleOptions}
              stateOptions={staffStateOptions}
              showAgencies={roleIsAssignableToAgency}
              agencyIds={agencyIds}
              agenciesOptions={agenciesOptions}
              showEmailInvitation={false}
              emailInviteState={NotificationState.NotSent}
              emailInviteDisabled
              onDirtyChange={setIsBoundFormDirty}
              onRolesChange={(roles) => {
                setStaffMemberForm((current) => ({ ...current, roles }));
                setSaveButtonState(SAVE_BUTTON_STATES.UNSAVED);
              }}
              onAgencyIdsChange={(value: string[]) => {
                if (!isEqual(agencyIds, value)) {
                  setAgencyIds(value);
                  setSaveButtonState(SAVE_BUTTON_STATES.UNSAVED);
                }
              }}
              onUploadPhoto={onUploadPhoto}
              onRemovePhoto={() => {
                removePhoto({
                  filePath: staffMemberForm.photo?.containerFilePath || '',
                });
              }}
            />

            {showWarningModal && (
              <RoleAssignmentModal
                setShowModal={setShowWarningModal}
                updateRoleAssignments={confirmCreateStaffMember}
                onCancel={() =>
                  resolveSubmission({
                    status: 'failed',
                    error: new Error('Role assignment was cancelled'),
                  })
                }
                selectedRoles={roleConflictRoles}
                roleConflicts={roleConflicts}
              />
            )}
          </AgxSpacer>
        )}
      </ContentLayout>
    </PageLayout>
  );
};

export default CreateStaffMember;
